// Part relationship candidates: what research may PROPOSE. Pure, no database.
//
// Read docs/apd-copilot/FEATURE_CHARTER.md and
// docs/part-intelligence/FEATURE_CHARTER.md before changing anything here.
//
// A chat answer that says "1706903 appears to be an alternate for 761574B" is
// not APD knowledge. It can become a candidate here, with its evidence and its
// grade, and only a person can confirm it. A confirmed candidate is the
// tenant's own knowledge; nothing promotes it to shared reference data, and it
// never rewrites the customer's identifier.
//
// Relationship names deliberately say "possible": confirming one records that a
// broker accepted it for APD's purposes, NOT that it is an approved substitute,
// airworthy, or interchangeable for a customer's installation.

import { z } from 'zod';

export const RELATIONSHIP_KINDS = [
  'possible_alternate',
  'possible_supersession',
  'possible_pma_replacement',
  'possible_oem',
  'possible_normalization',
  'related_part_number',
] as const;
export type RelationshipKind = (typeof RELATIONSHIP_KINDS)[number];

export const RELATIONSHIP_LABEL: Record<RelationshipKind, string> = {
  possible_alternate: 'Possible alternate',
  possible_supersession: 'Possible supersession',
  possible_pma_replacement: 'Possible PMA replacement',
  possible_oem: 'Possible OEM',
  possible_normalization: 'Possible formatting variant',
  related_part_number: 'Related part number',
};

/** Evidence grades from the APD part-sourcer specification.
 *  A: OEM, FAA, DLA, regulatory record, approved manual.
 *  B: authorized distributor, professional aviation database, technical record.
 *  C: agreement among several established independent suppliers.
 *  D: one listing, scraped catalog, social post or search snippet. */
export const EVIDENCE_GRADES = ['A', 'B', 'C', 'D'] as const;
export type EvidenceGrade = (typeof EVIDENCE_GRADES)[number];

export const CANDIDATE_STATUSES = ['proposed', 'confirmed', 'rejected', 'withdrawn'] as const;
export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number];

export const CandidateEvidence = z.object({
  kind: z.string().max(40),
  label: z.string().max(300),
  url: z.string().url().max(2000).nullable().optional(),
  excerpt: z.string().max(600).nullable().optional(),
  ref: z.string().max(120).nullable().optional(),
  grade: z.enum(EVIDENCE_GRADES).nullable().optional(),
});
export type CandidateEvidence = z.infer<typeof CandidateEvidence>;

export const CandidateInput = z.object({
  lineItemPublicId: z.string().regex(/^li_[0-9A-Za-z]{10,40}$/).nullable().optional(),
  subjectIdentifier: z.string().trim().min(1).max(100),
  relationship: z.enum(RELATIONSHIP_KINDS),
  proposedValue: z.string().trim().min(1).max(200),
  rationale: z.string().trim().min(1).max(1500),
  grade: z.enum(EVIDENCE_GRADES),
  evidence: z.array(CandidateEvidence).min(1).max(10),
  /** Copilot message the proposal came from, when it did. */
  messageId: z.string().regex(/^cpm_[0-9A-Za-z]{10,40}$/).nullable().optional(),
});
export type CandidateInput = z.infer<typeof CandidateInput>;

export const CandidateReviewInput = z.object({
  value: z.enum(['confirmed', 'rejected', 'withdrawn']),
  note: z.string().trim().max(1500).optional(),
});

/** Weak public evidence can never support an interchangeability-type claim. */
export function gradeAllowsRelationship(grade: EvidenceGrade, rel: RelationshipKind): boolean {
  if (grade !== 'D') return true;
  return rel === 'related_part_number' || rel === 'possible_normalization' || rel === 'possible_oem';
}
